/**
 * SimpleModal — reusable centered modal shell using nb-token styles.
 *
 * Groundwork for future modal adoption; NOT yet wired into any existing
 * modal (ReassignConfirmModal, WorkshopAuditLogModal, etc.). Adoption is a
 * separate slice.
 *
 * Props:
 *   open        — controls visibility; renders nothing when false
 *   onClose     — called on X button, backdrop click, or Escape
 *   title       — heading text, also the accessible label for the dialog
 *   children    — body content
 *   width       — panel width in px (default 520)
 *   footer      — optional footer slot rendered below the body
 *
 * Behavior:
 *   - Escape closes
 *   - Backdrop click closes
 *   - Clicking inside the panel does NOT close
 *   - role="dialog", aria-modal, aria-labelledby wired to the title heading
 */
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useId } from 'react';

import CloseButton from '../../shared/CloseButton';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number;
  footer?: ReactNode;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(20,28,22,0.4)',
  padding: 24,
};

export function SimpleModal({ open, onClose, title, children, width = 520, footer }: Props) {
  const titleId = useId();

  // Escape key closes the modal.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={overlayStyle}
      onClick={onClose}
    >
      <div
        className="nb-card"
        style={{
          width,
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px 12px',
            borderBottom: '1px solid var(--nb-border)',
          }}
        >
          <h2
            id={titleId}
            className="nb-display"
            style={{ margin: 0, fontSize: 16, color: 'var(--nb-bark-dark)' }}
          >
            {title}
          </h2>
          <CloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>{children}</div>

        {/* Footer (optional) */}
        {footer && (
          <div
            style={{
              borderTop: '1px solid var(--nb-border)',
              padding: '10px 16px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
